import React, { useState, useEffect } from 'react';
import { Award, Compass, Target, Shield, Heart, Star } from 'lucide-react';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../Firebase';
import { defaultChairmanData, defaultCeoData, mockBoardOfDirectors } from '../utils/seedData';

export default function About() {
  const [visibleItems, setVisibleItems] = useState({});
  const [chairman, setChairman] = useState(defaultChairmanData);
  const [ceo, setCeo] = useState(defaultCeoData);
  const [directors, setDirectors] = useState(mockBoardOfDirectors);

  // Fetch dynamic leadership data
  useEffect(() => {
    const fetchLeadership = async () => {
      try {
        // Fetch Chairman
        const chairSnap = await getDoc(doc(db, 'leadership', 'chairman'));
        if (chairSnap.exists()) {
          setChairman({ ...defaultChairmanData, ...chairSnap.data() });
        }

        // Fetch CEO
        const ceoSnap = await getDoc(doc(db, 'leadership', 'ceo'));
        if (ceoSnap.exists()) {
          setCeo({ ...defaultCeoData, ...ceoSnap.data() });
        }

        // Fetch Directors
        const dirSnap = await getDocs(collection(db, 'directors'));
        const fbDirectors = [];
        dirSnap.forEach((d) => fbDirectors.push({ id: d.id, ...d.data() }));

        if (fbDirectors.length > 0) {
          fbDirectors.sort((a, b) => (Number(a.order) || 99) - (Number(b.order) || 99));
          setDirectors(fbDirectors);
        } else {
          setDirectors(mockBoardOfDirectors);
        }
      } catch (error) {
        console.warn("Could not fetch leadership from Firestore, using defaults:", error);
      }
    };

    fetchLeadership();
  }, []);

  useEffect(() => {
    let observer;
    let elements;

    const timer = setTimeout(() => {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const index = entry.target.getAttribute('data-index');
              setVisibleItems((prev) => ({ ...prev, [index]: true }));
            }
          });
        },
        { threshold: 0.1, rootMargin: '0px 0px -100px 0px' }
      );

      elements = document.querySelectorAll('.timeline-item');
      elements.forEach((el) => observer.observe(el));
    }, 150);

    return () => {
      clearTimeout(timer);
      if (observer && elements) {
        elements.forEach((el) => observer.unobserve(el));
      }
    };
  }, []);

  const coreValues = [
    { name: 'Integrity', desc: 'Operating with strict compliance, ethical practices, and uncompromised banking transparency.', icon: Shield },
    { name: 'Customer First', desc: 'Designing tailored deposit portfolios and crop/MSME credits that serve the members first.', icon: Heart },
    { name: 'Member Trust', desc: 'Securing transaction accounts with high-grade encryptions and RBI cyber guidelines.', icon: Award },
    { name: 'Community Upliftment', desc: 'Reinvesting cooperative shares into rural block empowerment, seed campaigns, and micro credits.', icon: Compass }
  ];

  const milestones = [
    { year: '2005', title: 'Foundation & Sangli Register', desc: 'Sahyadri Urban registration under Co-operative Societies Act. Opened Head Office.' },
    { year: '2010', title: '₹50 Cr Deposit Milestone', desc: 'Expanded branches to Sangli and Kolhapur. Gained trust of 5,000+ local retail members.' },
    { year: '2018', title: 'Core Banking Software Integration', desc: 'Migrated branch ledger registers to digital servers with centralized core banking software.' },
    { year: '2024', title: 'Vanguard Secure Member Portal', desc: 'Launched Sahyadri Secure Member Portal, branch networks, and SMS alert system.' }
  ];

  return (
    <div className="pt-[68px] bg-[#F7F9FC]">
      {/* Page Header */}
      <section
        className="relative overflow-hidden text-navy py-20 px-4 text-center bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: "url('/page header.png')" }}
      >
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom,rgba(0,0,0,0.01),transparent)] pointer-events-none" />
        <div className="max-w-4xl mx-auto space-y-4 relative z-10">
          <span className="text-xs font-bold text-[#B31825] uppercase tracking-widest bg-[#B31825]/5 px-3 py-1 rounded-full w-fit mx-auto block">About Our Institution</span>
          <h1 className="font-montserrat font-extrabold text-3xl sm:text-4xl text-navy">Heritage of Trust, Vision of Innovation</h1>
          <p className="text-slate-600 text-sm sm:text-base font-normal max-w-2xl mx-auto">
            Founded in 2005, Sahyadri Urban Co-operative Credit Society Ltd. has been the catalyst for agricultural empowerment and financial wellness across Sangli, Sangli, Pune, and neighboring areas.
          </p>
        </div>
      </section>

      {/* History & Overview */}
      <section className="py-24 bg-white text-navy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-6 space-y-6">
            <span className="text-xs font-bold text-[#B31825] uppercase tracking-widest bg-[#B31825]/5 px-3 py-1 rounded-full w-fit block">Our Beginnings</span>
            <h2 className="font-montserrat font-extrabold text-3xl text-navy">Empowering Communities Since 2005</h2>
            <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
              For over two decades, Sahyadri Urban Co-operative Credit Society has remained committed to cooperative banking principles. Registered under the cooperative registrar rules (Reg No. STR/MH-789-2005), we began operation with a small treasury in Sangli district, focusing primarily on short-term crop advances and secure savings alternatives for retail merchants.
            </p>
            <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
              Today, we have grown to a multibranch network with deposits exceeding ₹340 Crores, driven by an unwavering bond of trust with our 18,500+ active members.
            </p>
          </div>

          <div className="lg:col-span-6 bg-[#F8FAFC] p-8 sm:p-10 rounded-3xl border border-gray-250/80 space-y-8">
            <div className="flex items-start space-x-5">
              <div className="p-3.5 bg-primary/10 text-primary rounded-2xl shrink-0">
                <Target className="h-6 w-6" />
              </div>
              <div>
                <h3 className="font-montserrat font-extrabold text-base text-navy">Our Mission</h3>
                <p className="text-xs sm:text-sm text-gray-500 mt-2 leading-relaxed font-light">
                  To provide secure deposit products, competitive interest yields, and hassle-free agricultural and trade credits to all societal blocks using simplified, transparent technology.
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-5 border-t border-gray-200 pt-8">
              <div className="p-3.5 bg-golden/10 text-golden-dark rounded-2xl shrink-0">
                <Compass className="h-6 w-6" />
              </div>
              <div>
                <h3 className="font-montserrat font-extrabold text-base text-navy">Our Vision</h3>
                <p className="text-xs sm:text-sm text-gray-500 mt-2 leading-relaxed font-light">
                  To transform into a premier digital-first cooperative bank, blending cooperative community trust with modern net-banking security and instant retail lending.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Chairman & CEO Messages (Dynamic) */}
      <section className="py-24 bg-neutralBg border-y border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
          {/* Chairman Message */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center bg-white border border-gray-200 rounded-3xl p-8 sm:p-12 shadow-md relative overflow-hidden">
            <div className="absolute top-0 right-0 w-24 h-24 bg-[#B31825]/5 rounded-bl-full" />
            <div className="lg:col-span-4 flex flex-col items-center text-center">
              {/* Portrait */}
              <div className="h-40 w-40 rounded-full bg-navy flex items-center justify-center text-golden overflow-hidden border-4 border-[#B31825] shadow-xl mb-4 select-none">
                {chairman.imageUrl ? (
                  <img
                    src={chairman.imageUrl}
                    alt={chairman.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <svg className="w-full h-full" viewBox="0 0 100 100" fill="none">
                    <rect width="100" height="100" fill="#020C1A" />
                    <circle cx="50" cy="40" r="18" fill="#B31825" />
                    <path d="M25 80 C25 60, 75 60, 75 80 Z" fill="#E5E7EB" />
                    <rect x="42" y="58" width="16" height="24" fill="#052046" />
                  </svg>
                )}
              </div>
              <h3 className="font-montserrat font-extrabold text-base text-navy">{chairman.name}</h3>
              <p className="text-[10px] font-bold text-[#B31825] uppercase tracking-widest mt-1">{chairman.role}</p>
            </div>
            <div className="lg:col-span-8 space-y-4">
              <span className="text-xs font-bold text-[#B31825] uppercase tracking-widest bg-[#B31825]/5 px-3 py-1 rounded-full w-fit block">Message from Chairman</span>
              <h3 className="font-montserrat font-extrabold text-2xl text-navy">{chairman.title}</h3>
              <p className="text-gray-500 text-xs sm:text-sm leading-relaxed italic font-light whitespace-pre-line">
                {chairman.message}
              </p>
            </div>
          </div>

          {/* CEO Message */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center bg-white border border-gray-200 rounded-3xl p-8 sm:p-12 shadow-md relative overflow-hidden">
            <div className="absolute top-0 left-0 w-24 h-24 bg-primary/5 rounded-br-full" />
            <div className="lg:col-span-8 order-2 lg:order-1 space-y-4">
              <span className="text-xs font-bold text-primary uppercase tracking-widest bg-primary/10 px-3 py-1 rounded-full w-fit block">Message from CEO / GM</span>
              <h3 className="font-montserrat font-extrabold text-2xl text-navy">{ceo.title}</h3>
              <p className="text-gray-500 text-xs sm:text-sm leading-relaxed italic font-light whitespace-pre-line">
                {ceo.message}
              </p>
            </div>
            <div className="lg:col-span-4 order-1 lg:order-2 flex flex-col items-center text-center">
              <div className="h-40 w-40 rounded-full bg-navy flex items-center justify-center text-golden overflow-hidden border-4 border-primary shadow-xl mb-4 select-none">
                {ceo.imageUrl ? (
                  <img
                    src={ceo.imageUrl}
                    alt={ceo.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <svg className="w-full h-full" viewBox="0 0 100 100" fill="none">
                    <rect width="100" height="100" fill="#0B1F3A" />
                    <circle cx="50" cy="40" r="18" fill="#D1D5DB" />
                    <path d="M25 80 C25 60, 75 60, 75 80 Z" fill="#F3F4F6" />
                    <rect x="42" y="58" width="16" height="24" fill="#00A86B" />
                  </svg>
                )}
              </div>
              <h3 className="font-montserrat font-extrabold text-base text-navy">{ceo.name}</h3>
              <p className="text-[10px] font-bold text-primary uppercase tracking-widest mt-1">{ceo.role}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Board of Directors Grid (Dynamic) */}
      <section className="py-24 bg-white text-navy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-3">
            <span className="text-xs font-bold text-[#B31825] uppercase tracking-widest bg-[#B31825]/5 px-3 py-1 rounded-full w-fit mx-auto block">Leadership Team</span>
            <h2 className="font-montserrat font-extrabold text-3xl text-navy">Board of Directors</h2>
            <p className="text-gray-500 text-sm sm:text-base leading-relaxed">
              Our Board brings rich expertise in rural economy, financial audits, and regulatory compliance.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {directors.map((director, i) => (
              <div key={director.id || i} className="bg-[#F8FAFC] border border-gray-200 rounded-3xl p-6 hover:bg-white hover:border-[#B31825] hover:shadow-xl transition-all duration-300 text-center space-y-4 flex flex-col justify-between">
                <div className="space-y-4">
                  <div className="h-24 w-24 rounded-full bg-navy mx-auto flex items-center justify-center text-white overflow-hidden shadow-lg border-2 border-golden/55 select-none">
                    {director.imageUrl ? (
                      <img
                        src={director.imageUrl}
                        alt={director.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <svg className="w-full h-full" viewBox="0 0 100 100" fill="none">
                        <rect width="100" height="100" fill="#020C1A" />
                        <circle cx="50" cy="40" r="18" fill="#B31825" />
                        <path d="M25 80 C25 60, 75 60, 75 80 Z" fill="#E5E7EB" />
                      </svg>
                    )}
                  </div>
                  <div>
                    <h4 className="font-montserrat font-bold text-base text-navy">{director.name}</h4>
                    <p className="text-[10px] font-bold text-[#B31825] uppercase tracking-widest mt-1">{director.role}</p>
                  </div>
                  <p className="text-gray-500 text-xs sm:text-sm leading-relaxed px-2 font-light">{director.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-24 bg-navy text-white relative border-y border-white/5">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.02),transparent)] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-3">
            <span className="text-xs font-bold text-golden uppercase tracking-widest bg-white/10 px-3 py-1 rounded-full w-fit mx-auto block">Core Anchors</span>
            <h2 className="font-montserrat font-extrabold text-3xl text-white">Our Organizational Values</h2>
            <p className="text-gray-300 text-sm font-light">Every transaction, account registration, and credit approval is bound by these values.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {coreValues.map((value, i) => {
              const Icon = value.icon;
              return (
                <div key={i} className="bg-white/5 border border-white/10 rounded-3xl p-6 hover:bg-white/10 transition-colors space-y-4 group">
                  <div className="p-3.5 bg-primary/20 text-golden rounded-xl w-fit group-hover:bg-golden group-hover:text-navy transition-all">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-montserrat font-bold text-lg text-white">{value.name}</h3>
                  <p className="text-gray-300 text-xs leading-relaxed font-light">{value.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* History Timeline */}
      <section className="py-24 bg-white text-navy overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-3">
            <span className="text-xs font-bold text-primary uppercase tracking-widest bg-primary/10 px-3 py-1 rounded-full w-fit mx-auto block">Milestones</span>
            <h2 className="font-montserrat font-extrabold text-3xl text-navy">Our History Timeline</h2>
            <p className="text-gray-500 text-sm sm:text-base font-light">Two decades of steady growth, security, and co-operative excellence.</p>
          </div>

          <div className="relative max-w-5xl mx-auto mt-16">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-gray-200 -translate-x-1/2" />

            <div className="space-y-12 relative">
              {milestones.map((ms, i) => {
                const isLeft = i % 2 === 0;
                return (
                  <div
                    key={i}
                    data-index={i}
                    className={`timeline-item relative flex flex-col md:flex-row ${
                      isLeft ? 'md:flex-row' : 'md:flex-row-reverse'
                    } items-center justify-between group transition-all duration-700 ease-out transform ${
                      visibleItems[i]
                        ? 'opacity-100 translate-y-0 scale-100'
                        : 'opacity-0 translate-y-12 scale-95 pointer-events-none'
                    }`}
                  >
                    <div className="absolute left-4 md:left-1/2 top-1/2 -translate-y-1/2 h-6 w-6 rounded-full bg-golden border-4 border-white shadow -translate-x-1/2 z-10 group-hover:bg-[#B31825] transition-colors duration-300" />

                    <div className={`w-full md:w-[calc(50%-2.5rem)] pl-12 md:pl-0 ${isLeft ? 'md:text-right' : 'md:text-left'}`}>
                      <div className="bg-[#F8FAFC] border border-gray-200 rounded-3xl p-6 sm:p-8 shadow-sm group-hover:bg-white group-hover:border-golden transition-all duration-300 transform group-hover:-translate-y-1">
                        <span className="font-montserrat font-extrabold text-lg text-primary block mb-1">{ms.year}</span>
                        <h3 className="font-montserrat font-bold text-base text-navy">{ms.title}</h3>
                        <p className="text-gray-500 text-xs sm:text-sm mt-2 leading-relaxed font-light">{ms.desc}</p>
                      </div>
                    </div>

                    <div className="hidden md:block w-[calc(50%-2.5rem)]" />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Awards & Recognition */}
      <section className="py-24 bg-neutralBg text-navy border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-3">
            <span className="text-xs font-bold text-primary uppercase tracking-widest bg-primary/10 px-3 py-1 rounded-full w-fit mx-auto block">Certificates & Merits</span>
            <h2 className="font-montserrat font-extrabold text-3xl text-navy">Awards & Recognition</h2>
            <p className="text-gray-500 text-sm sm:text-base font-light">Validating our dedication towards rural development and audit excellence.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white border border-gray-200 rounded-3xl p-6 sm:p-8 shadow-sm flex flex-col justify-between space-y-5 hover:shadow-md transition-shadow">
              <div className="text-golden text-2xl font-bold flex items-center">
                <Star className="h-6 w-6 text-golden fill-current mr-2" />
                <span>Grade A Audit Class</span>
              </div>
              <p className="text-gray-500 text-xs sm:text-sm leading-relaxed font-light">
                Consistently awarded Grade 'A' Audit Rating by the cooperative department auditor office, Sangli, for pristine balance sheets and clean audit logs.
              </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-3xl p-6 sm:p-8 shadow-sm flex flex-col justify-between space-y-5 hover:shadow-md transition-shadow">
              <div className="text-golden text-2xl font-bold flex items-center">
                <Star className="h-6 w-6 text-golden fill-current mr-2" />
                <span>Rural Credit Leader</span>
              </div>
              <p className="text-gray-500 text-xs sm:text-sm leading-relaxed font-light">
                Recognized at the Cooperative Union Summit for outstanding agricultural credit reach, disbursing crop & gold micro loans to 10,000+ farmer members.
              </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-3xl p-6 sm:p-8 shadow-sm flex flex-col justify-between space-y-5 hover:shadow-md transition-shadow">
              <div className="text-golden text-2xl font-bold flex items-center">
                <Star className="h-6 w-6 text-golden fill-current mr-2" />
                <span>Digital Adopter Award</span>
              </div>
              <p className="text-gray-500 text-xs sm:text-sm leading-relaxed font-light">
                Appreciated for pioneering modern core banking migration, enabling online ledger updates, instant SMS verification, and UPI services in cooperative credit fields.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
